/**
 * Exact shortlisting from plan-availability totals, measuring only the resources
 * the shortlist can depend on.
 *
 * The summary read gives every resource in the pool an upper bound on the free
 * hours it has once measured against local capacity: measuring only ever takes
 * hours away, beyond a little rounding slack the bound already includes. Every
 * other ranking key is known locally for the whole pool, so a candidate whose
 * best possible position is behind the resource already next in line cannot
 * change the order, and is never measured.
 */

/** A resource the shortlist may take, with the most free hours it can have. */
export interface ShortlistCandidate {
  id: string;
  /** Position in the list the full-detail path would rank, which breaks ties. */
  index: number;
  upperBound: number;
}

/** What a comparator ranks: a candidate at a given number of free hours. */
export interface RankedEntry {
  id: string;
  index: number;
  hours: number;
}

/** Orders entries best first; must break every tie on `index`. */
export type RankComparator = (a: RankedEntry, b: RankedEntry) => number;

/**
 * The measured resources, shared by every ordering over one plan so nothing is
 * measured twice. A measurement of `null` is a resource with no free time left.
 */
export class MeasuredPool<T> {
  private readonly measured = new Map<string, T | null>();

  constructor(
    private readonly measureBatch: (ids: string[]) => Promise<Map<string, T>>,
    readonly batchSize = 12,
  ) {}

  has(id: string): boolean {
    return this.measured.has(id);
  }

  get(id: string): T | null | undefined {
    return this.measured.get(id);
  }

  /** Measures the ids not yet measured, `batchSize` at a time. */
  async measure(ids: string[]): Promise<void> {
    const pending = [...new Set(ids)].filter(id => !this.measured.has(id));
    for (let i = 0; i < pending.length; i += this.batchSize) {
      const batch = pending.slice(i, i + this.batchSize);
      const results = await this.measureBatch(batch);
      for (const id of batch) this.measured.set(id, results.get(id) ?? null);
    }
  }
}

/** Yields the candidates in their exact ranked order, one at a time. */
export interface OrderedTaker<T> {
  next(): Promise<T | undefined>;
}

/**
 * The candidates in the order `compare` gives their measured hours, without
 * measuring any candidate that cannot come next.
 *
 * The best measured survivor is taken once no unmeasured candidate's upper bound
 * could rank ahead of it. Until then the unmeasured candidates that could rank
 * best are measured, a batch at a time — including when a batch is taken out
 * entirely by local capacity.
 */
export function orderedTaker<T>(
  pool: MeasuredPool<T>,
  candidates: ShortlistCandidate[],
  compare: RankComparator,
  hoursOf: (measured: T) => number,
): OrderedTaker<T> {
  const optimistic = [...candidates].sort((a, b) => compare(
    { id: a.id, index: a.index, hours: a.upperBound },
    { id: b.id, index: b.index, hours: b.upperBound },
  ));
  const taken = new Set<string>();
  let cursor = 0;

  return {
    async next() {
      for (;;) {
        while (cursor < optimistic.length && pool.has(optimistic[cursor].id)) cursor++;
        const frontier = optimistic[cursor];

        let best: { entry: RankedEntry; value: T } | undefined;
        for (const candidate of candidates) {
          if (taken.has(candidate.id)) continue;
          const value = pool.get(candidate.id);
          if (value == null) continue;
          const entry = { id: candidate.id, index: candidate.index, hours: hoursOf(value) };
          if (!best || compare(entry, best.entry) < 0) best = { entry, value };
        }

        const frontierCanOutrank = frontier !== undefined && (!best || compare(
          { id: frontier.id, index: frontier.index, hours: frontier.upperBound },
          best.entry,
        ) < 0);
        if (best && !frontierCanOutrank) {
          taken.add(best.entry.id);
          return best.value;
        }
        if (!frontier) return undefined;

        const batch: string[] = [];
        for (let i = cursor; i < optimistic.length && batch.length < pool.batchSize; i++) {
          if (!pool.has(optimistic[i].id)) batch.push(optimistic[i].id);
        }
        await pool.measure(batch);
      }
    },
  };
}

/** The first `count` candidates in ranked order (fewer when fewer survive). */
export async function takeRanked<T>(taker: OrderedTaker<T>, count: number): Promise<T[]> {
  const taken: T[] = [];
  while (taken.length < count) {
    const next = await taker.next();
    if (next === undefined) break;
    taken.push(next);
  }
  return taken;
}

/**
 * The summed measured hours of `candidates` in `index` order, or `'enough'` once
 * the ones measured so far already reach `target` — the rest can only add to it.
 * Measures the candidates with the most hours first.
 */
export async function pooledHoursUpTo<T>(
  pool: MeasuredPool<T>,
  candidates: ShortlistCandidate[],
  target: number,
  hoursOf: (measured: T) => number,
): Promise<number | 'enough'> {
  const byBound = [...candidates].sort((a, b) => (b.upperBound - a.upperBound) || (a.index - b.index));
  for (;;) {
    let measuredHours = 0;
    const unmeasured: string[] = [];
    for (const candidate of byBound) {
      if (!pool.has(candidate.id)) {
        unmeasured.push(candidate.id);
        continue;
      }
      const value = pool.get(candidate.id);
      if (value != null) measuredHours += hoursOf(value);
    }
    // A margin, so a total reached in another order than the full path sums
    // it in is never mistaken for one that reaches the target.
    if (measuredHours >= target + 1e-6) return 'enough';
    if (unmeasured.length === 0) {
      return [...candidates]
        .sort((a, b) => a.index - b.index)
        .reduce((sum, candidate) => {
          const value = pool.get(candidate.id);
          return value == null ? sum : sum + hoursOf(value);
        }, 0);
    }
    await pool.measure(unmeasured.slice(0, pool.batchSize));
  }
}
